'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRolePermissions } from '@/hooks/useRolePermissions';

interface NavItem {
  label: string;
  href: string;
  icon: string;
  badgeKey?: string;
  group?: string;
  permissionKey?: string;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/', icon: 'ChartBarIcon', group: 'Overview' },
  { label: 'Lead Management', href: '/lead-management', icon: 'UserGroupIcon', badgeKey: 'leads', group: 'Operations' },
  { label: 'Architect Management', href: '/architect-management', icon: 'HomeModernIcon', group: 'Operations' },
  { label: 'Schedule', href: '/schedule', icon: 'CalendarIcon', group: 'Operations' },
  { label: 'User Management', href: '/user-management', icon: 'UsersIcon', group: 'Operations', permissionKey: 'canAccessUserManagement' },
  { label: 'Org Tree', href: '/org-tree', icon: 'ShareIcon', group: 'Operations', permissionKey: 'canAccessUserManagement' },
  { label: 'Product Management', href: '/product-management', icon: 'CubeIcon', group: 'Catalog', permissionKey: 'canAccessProductManagement' },
  { label: 'Reports & Analytics', href: '/reports-analytics', icon: 'ChartPieIcon', group: 'Insights', permissionKey: 'canAccessReports' },
];

const roleDisplayMap: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  calling_executive: 'Calling Executive',
  marketing_executive: 'Marketing Executive',
  sales_executive: 'Sales Executive',
};

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onMobileClose?: () => void;
}

export default function Sidebar({ collapsed, onToggle, onMobileClose }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const [leadCount, setLeadCount] = useState<number>(0);
  const { userProfile, userRole, signOut } = useAuth();
  const permissions = useRolePermissions(userRole);

  useEffect(() => {
    const supabase = createClient();

    const fetchLeadCount = async () => {
      const { count } = await supabase
        .from('leads')
        .select('*', { count: 'exact', head: true });
      setLeadCount(count ?? 0);
    };

    fetchLeadCount();

    const channel = supabase
      .channel('sidebar-lead-count')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads' }, () => {
        fetchLeadCount();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const getBadgeCount = (item: NavItem): number => {
    if (item.badgeKey === 'leads') return leadCount;
    return 0;
  };

  // Filter nav items based on permissions
  const visibleNavItems = navItems.filter((item) => {
    if (!item.permissionKey) return true;
    return permissions[item.permissionKey as keyof typeof permissions] === true;
  });

  const groups = Array.from(new Set(visibleNavItems.map((i) => i.group)));

  const displayName = userProfile?.full_name || 'User';
  const displayRole = roleDisplayMap[userRole ?? ''] || 'Executive';

  const handleNavClick = () => {
    if (onMobileClose) onMobileClose();
  };

  return (
    <aside
      className={`h-full flex flex-col z-30 sidebar-transition overflow-hidden ${
        collapsed ? 'w-16' : 'w-60'
      }`}
      style={{ backgroundColor: '#1C1C2E' }}
    >
      {/* Logo */}
      <div
        className={`flex items-center h-16 ${collapsed ? 'justify-center px-0' : 'px-4 gap-3'}`}
        style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
      >
        <Link href="/" className="flex items-center gap-3" onClick={handleNavClick}>
          <AppLogo size={32} />
          {!collapsed && (
            <span className="font-bold text-white text-lg tracking-tight">JMCAdmin</span>
          )}
        </Link>
        {/* Close button on mobile */}
        {!collapsed && onMobileClose && (
          <button
            onClick={onMobileClose}
            className="ml-auto p-1.5 rounded-lg lg:hidden"
            style={{ color: 'rgba(255,255,255,0.5)' }}
            aria-label="Close menu"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin py-4 px-2">
        {groups.map((group) => (
          <div key={`group-${group}`} className="mb-4">
            {!collapsed && (
              <p
                className="text-xs font-semibold uppercase tracking-widest px-3 mb-1"
                style={{ color: 'rgba(255,255,255,0.35)' }}
              >
                {group}
              </p>
            )}
            {visibleNavItems
              .filter((i) => i.group === group)
              .map((item) => {
                const isActive =
                  item.href === '/' ? pathname === '/' : pathname.startsWith(item.href);
                const badgeCount = getBadgeCount(item);
                return (
                  <Link
                    key={`nav-${item.href}`}
                    href={item.href}
                    title={collapsed ? item.label : undefined}
                    className={`nav-item mb-0.5 relative group`}
                    style={{
                      backgroundColor: isActive ? 'rgba(196,30,58,0.20)' : 'transparent',
                      color: isActive ? '#ffffff' : 'rgba(255,255,255,0.70)',
                    }}
                    onClick={handleNavClick}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.08)';
                        (e.currentTarget as HTMLElement).style.color = '#ffffff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                        (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.70)';
                      }
                    }}
                  >
                    <Icon
                      name={item.icon as Parameters<typeof Icon>[0]['name']}
                      size={20}
                      className={isActive ? 'text-primary' : 'text-current'}
                    />
                    {!collapsed && <span className="flex-1 text-sm">{item.label}</span>}
                    {!collapsed && badgeCount > 0 ? (
                      <span className="bg-primary text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                        {badgeCount}
                      </span>
                    ) : null}
                    {collapsed && badgeCount > 0 ? (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
                    ) : null}
                    {/* Tooltip for collapsed */}
                    {collapsed && (
                      <span
                        className="absolute left-full ml-2 px-2 py-1 text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50"
                        style={{ backgroundColor: '#1C1C2E', border: '1px solid rgba(255,255,255,0.12)' }}
                      >
                        {item.label}
                      </span>
                    )}
                  </Link>
                );
              })}
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }} className="p-2">
        <button
          onClick={async () => { try { await signOut(); router.push('/login'); } catch (e) { router.push('/login'); } }}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg transition-all duration-150 mt-1"
          style={{ color: 'rgba(255,255,255,0.60)' }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(239,68,68,0.15)';
            (e.currentTarget as HTMLElement).style.color = '#f87171';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
            (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.60)';
          }}
          aria-label="Sign out"
        >
          <Icon name="ArrowRightOnRectangleIcon" size={18} />
          {!collapsed && <span className="text-xs">Sign Out</span>}
        </button>
      </div>
    </aside>
  );
}